import React, { useState, useEffect, useCallback } from "react";
import { LineChart, Wallet, Target, DollarSign, Percent, TrendingUp, ArrowRight, ArrowUpRight, ArrowDownRight, Minus, Download, FileText, CalendarClock, Send, Save } from "lucide-react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import api, { formatApiError } from "@/lib/api";
import ProvenanceBadges, { SimulationBadge } from "@/components/provenance/ProvenanceBadges";
import { useAuth } from "@/auth/AuthContext";
import { useDemo, DemoHint } from "@/components/demo/DemoUi";
import DemoCampaignReport from "@/components/demo/DemoCampaignReport";
import { toast } from "sonner";
import { isMauritiusDestination, MAURITIUS_DEMO_REPORT } from "@/lib/mauritiusDemoJourney";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const statusChip = {
  measured: { label: "Measured", cls: "bg-[#DCFCE7] text-[#047857]" },
  estimated: { label: "Estimated", cls: "bg-[#FEF3C7] text-[#B45309]" },
  unavailable: { label: "Unavailable", cls: "bg-slate-100 text-slate-400" },
};

const nf = new Intl.NumberFormat("fr-FR");
const fmtMoney = (v, cur) => v == null ? "—" : `${nf.format(Math.round(v))} ${cur || ""}`;
const fmtNum = (v) => v == null ? "—" : nf.format(v);
const fmtPct = (v) => v == null ? "—" : `${(v * 100).toFixed(1)}%`;
const fmtRoi = (v) => v == null ? "—" : `${v >= 0 ? "+" : ""}${(v * 100).toFixed(1)}%`;

const StatusChip = ({ status, testid }) => {
  const s = statusChip[status] || statusChip.unavailable;
  return <span data-testid={testid} className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${s.cls}`}>{s.label}</span>;
};

const METRICS = [
  { key: "investment", label: "Investment", icon: Wallet, fmt: (b, d) => fmtMoney(b.investment.value, d.currency), get: (d) => d.investment },
  { key: "results", label: "Results", icon: Target, fmt: (b) => b.results.value == null ? "—" : `${fmtNum(b.results.value)} ${b.results.label}`, get: (d) => d.results },
  { key: "revenue", label: "Revenue", icon: DollarSign, fmt: (b, d) => fmtMoney(b.revenue.value, d.currency), get: (d) => d.revenue },
  { key: "conversion", label: "Conversion", icon: Percent, fmt: (b) => fmtPct(b.conversion.value), get: (d) => d.conversion },
  { key: "roi", label: "ROI", icon: TrendingUp, fmt: (b) => fmtRoi(b.roi.value), get: (d) => d.roi },
];

function MetricCard({ m, block, currency }) {
  const cell = m.get(block);
  const Icon = m.icon;
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={`metric-${m.key}`}>
      <div className="mb-2 flex items-center justify-between">
        <span className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-400"><Icon size={13} /> {m.label}</span>
        <StatusChip status={cell.status} testid={`status-${m.key}`} />
      </div>
      <p className="font-heading text-2xl font-bold text-[#17201A]">{m.fmt(block, { currency })}</p>
      {cell.formula && cell.status !== "unavailable" && <p className="mt-1 font-mono text-[10px] text-slate-400">{cell.formula}</p>}
      {m.key === "revenue" && cell.source === "data_hub" && <p className="mt-1 text-[10px] text-[#B45309]">Revenu importé du Data Hub · attribution {cell.attribution}</p>}
      {cell.status === "unavailable" && <p className="mt-1 text-[11px] text-slate-400">Donnée insuffisante — non calculée.</p>}
    </div>
  );
}

const Delta = ({ a, b, kind }) => {
  if (a == null || b == null) return <Minus size={14} className="text-slate-300" />;
  const diff = b - a;
  if (Math.abs(diff) < 1e-9) return <Minus size={14} className="text-slate-300" />;
  const up = diff > 0;
  const Icon = up ? ArrowUpRight : ArrowDownRight;
  const text = kind === "pct" ? `${up ? "+" : ""}${(diff * 100).toFixed(1)} pts` : (kind === "money" || kind === "num") ? `${up ? "+" : ""}${nf.format(Math.round(diff))}` : "";
  return <span className={`inline-flex items-center gap-0.5 text-xs font-semibold ${up ? "text-[#047857]" : "text-rose-600"}`}><Icon size={13} /> {text}</span>;
};

function MauritiusPerformanceExample() {
  const report = MAURITIUS_DEMO_REPORT;
  const exportExamplePdf = () => {
    const doc = new jsPDF();
    doc.setFontSize(16); doc.setTextColor("#1E3D2B");
    doc.text("Lodge & Co — Rapport de campagne exemple", 14, 18);
    doc.setFontSize(10); doc.setTextColor("#64748B");
    doc.text("SIMULATION — résultats illustratifs, non observés et non prédictifs", 14, 25);
    doc.text(report.campaign, 14, 32);
    doc.text(`Période : ${report.period}`, 14, 38);
    autoTable(doc, {
      startY: 46,
      head: [["Indicateur", "Valeur", "Statut"]],
      body: report.metrics.map((metric) => [metric.label, metric.value, "Simulation"]),
      headStyles: { fillColor: [30, 61, 43] },
      styles: { fontSize: 9 },
    });
    doc.save("lodge-co-maurice-rapport-campagne-exemple.pdf");
  };
  return (
    <div className="rounded-2xl border border-violet-200 bg-violet-50/40 p-5" data-testid="mauritius-performance-example">
      <div className="flex flex-wrap items-center gap-2">
        <SimulationBadge />
        <span className="text-xs font-semibold uppercase tracking-wide text-violet-700">Rapport campagne exemple</span>
      </div>
      <h2 className="mt-3 font-heading text-lg font-semibold text-[#17201A]">{report.campaign}</h2>
      <p className="mt-1 text-xs text-violet-700">{report.period} · Résultats illustratifs, non observés et non prédictifs.</p>
      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
        {report.metrics.map((metric) => (
          <div key={metric.key} data-testid={`demo-report-${metric.key}`} className="rounded-xl border border-violet-100 bg-white p-4">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{metric.label}</p>
            <p className="mt-1 font-heading text-xl font-bold text-[#17201A]">{metric.value}</p>
            <span className="mt-2 inline-flex rounded-full bg-violet-100 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide text-violet-700">Simulation</span>
          </div>
        ))}
      </div>
      <p className="mt-4 text-xs leading-relaxed text-slate-500">
        Cette vue montre la forme du rapport final. Les valeurs réelles apparaîtront uniquement après connexion du tracking et collecte de KPIs mesurés.
      </p>
      <button type="button" data-testid="download-mauritius-demo-report" onClick={exportExamplePdf}
        className="mt-4 inline-flex items-center gap-1.5 rounded-lg border border-violet-200 bg-white px-3 py-2 text-sm font-semibold text-violet-800 hover:bg-violet-50">
        <FileText size={15} /> Télécharger le rapport exemple (PDF)
      </button>
    </div>
  );
}

export default function Performance() {
  const { activeOrgId, activeMembership } = useAuth();
  const { readOnly } = useDemo();
  const admin = ["Owner", "Administrator"].includes(activeMembership?.role);
  const [data, setData] = useState(null);
  const [pa, setPa] = useState("");
  const [pb, setPb] = useState("");
  const [sched, setSched] = useState({ frequency: "monthly", enabled: false, recipients: [], last_sent_at: null });
  const [sends, setSends] = useState([]);
  const [recipientsText, setRecipientsText] = useState("");

  const loadSchedule = useCallback(async () => {
    if (!activeOrgId) return;
    try {
      const { data } = await api.get(`/organizations/${activeOrgId}/report-schedule`);
      setSched(data.schedule);
      setSends(data.sends || []);
      setRecipientsText((data.schedule.recipients || []).join(", "));
    } catch { /* noop */ }
  }, [activeOrgId]);
  useEffect(() => { loadSchedule(); }, [loadSchedule]);

  const saveSchedule = async (patch) => {
    const recipients = recipientsText.split(",").map((e) => e.trim()).filter((e) => e.includes("@"));
    const payload = { frequency: sched.frequency, enabled: sched.enabled, recipients, ...patch };
    setSched((s) => ({ ...s, ...payload }));
    try { await api.put(`/organizations/${activeOrgId}/report-schedule`, payload); toast.success("Planification enregistrée"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail, err.message)); }
  };
  const sendNow = async () => {
    try {
      const { data } = await api.post(`/organizations/${activeOrgId}/report-schedule/send-now`);
      setSends((s) => [data.send, ...s]);
      toast.success("Rapport envoyé (simulé)");
      loadSchedule();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail, err.message)); }
  };

  const load = useCallback(async () => {
    if (!activeOrgId) return;
    const { data } = await api.get(`/organizations/${activeOrgId}/performance`);
    setData(data);
    if (data.periods?.length >= 2) { setPa(data.periods[0]); setPb(data.periods[1]); }
    else if (data.periods?.length === 1) { setPa(data.periods[0]); }
  }, [activeOrgId]);
  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="py-20 text-center text-sm text-slate-400" data-testid="performance-loading">Chargement…</div>;

  const overall = data.overall;
  const noCampaigns = overall.campaigns_count === 0;
  const showMauritiusExample = noCampaigns && !readOnly && isMauritiusDestination(activeMembership?.org || {});
  const blockA = pa ? data.by_period[pa] : null;
  const blockB = pb ? data.by_period[pb] : null;
  const kindOf = (k) => k === "conversion" ? "pct" : k === "roi" ? "pct" : k === "revenue" || k === "investment" ? "money" : "num";

  // Build export rows: metric label + value + status for the chosen blocks.
  const cellText = (m, block) => block ? `${m.fmt(block, { currency: block.currency })} (${m.get(block).status})` : "—";
  const exportColumns = () => {
    if (blockA && blockB) return { headers: ["Indicateur", pa, pb], blocks: [blockA, blockB] };
    return { headers: ["Indicateur", "Vue d'ensemble"], blocks: [overall] };
  };
  const exportCSV = () => {
    const { headers, blocks } = exportColumns();
    const rows = [headers, ...METRICS.map((m) => [m.label, ...blocks.map((b) => cellText(m, b))])];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" }));
    const a = document.createElement("a");
    a.href = url; a.download = "performance-lodge-co.csv"; a.click();
    URL.revokeObjectURL(url);
  };
  const exportPDF = () => {
    const { headers, blocks } = exportColumns();
    const doc = new jsPDF();
    doc.setFontSize(16); doc.setTextColor("#1E3D2B");
    doc.text("Lodge & Co — Performance", 14, 18);
    doc.setFontSize(10); doc.setTextColor("#64748B");
    doc.text(`Fiabilité : measured / estimated / unavailable · ${new Date().toLocaleDateString("fr-FR")}`, 14, 25);
    autoTable(doc, {
      startY: 32,
      head: [headers],
      body: METRICS.map((m) => [m.label, ...blocks.map((b) => cellText(m, b))]),
      headStyles: { fillColor: [30, 61, 43] },
      styles: { fontSize: 9 },
    });
    doc.save("performance-lodge-co.pdf");
  };

  return (
    <div className="space-y-8" data-testid="performance-page">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2.5">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-[#1E3D2B] to-[#2C5540]"><LineChart size={18} className="text-[#5C9873]" /></div>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="font-heading text-2xl font-bold tracking-tight text-[#17201A]">Performance</h1>
              <ProvenanceBadges meta={overall} />
            </div>
            <p className="text-sm text-[#64748B]">Indicateurs de campagne — seuls les ratios justifiables sont calculés, jamais inventés.</p>
          </div>
        </div>
        {!noCampaigns && (
          <div className="flex items-center gap-2">
            <button data-testid="export-csv" onClick={exportCSV} className="inline-flex items-center gap-1.5 rounded-lg border border-[#E8E5DD] bg-white px-3 py-2 text-sm font-semibold text-[#475569] hover:bg-[#FAFAF6]"><Download size={15} /> CSV</button>
            <button data-testid="export-pdf" onClick={exportPDF} className="inline-flex items-center gap-1.5 rounded-lg bg-[#1E3D2B] px-3 py-2 text-sm font-semibold text-white hover:bg-[#2C5540]"><FileText size={15} /> PDF</button>
          </div>
        )}
      </div>

      <DemoCampaignReport />

      {/* legend */}
      <div className="flex flex-wrap items-center gap-3 rounded-xl border border-[#E8E5DD] bg-[#FAFAF6] px-4 py-2.5 text-xs text-[#5A5347]" data-testid="perf-legend">
        <span className="font-semibold">Fiabilité :</span>
        <span className="flex items-center gap-1.5"><StatusChip status="measured" /> issu directement des données</span>
        <span className="flex items-center gap-1.5"><StatusChip status="estimated" /> dérivé d'un proxy (budget, bookings)</span>
        <span className="flex items-center gap-1.5"><StatusChip status="unavailable" /> donnée absente</span>
      </div>

      {noCampaigns ? (
        <div className="space-y-4" data-testid="performance-empty">
          {showMauritiusExample && <MauritiusPerformanceExample />}
          <div className="rounded-2xl border border-slate-200 bg-white py-10 text-center">
            <LineChart size={34} className="mx-auto mb-3 text-slate-300" />
            <p className="text-sm font-medium text-slate-600">Aucune campagne réelle à analyser</p>
            <p className="mx-auto mt-1 max-w-sm text-sm text-slate-400">Créez des campagnes avec des KPIs (spend, leads, conversions, bookings, revenue) pour voir la performance.</p>
          </div>
        </div>
      ) : (
        <>
          {/* Overall */}
          <div>
            <h2 className="mb-3 text-sm font-bold text-[#17201A]">Vue d'ensemble <span className="font-normal text-slate-400">· {overall.campaigns_count} campagne{overall.campaigns_count > 1 ? "s" : ""}</span></h2>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
              {METRICS.map((m) => <MetricCard key={m.key} m={m} block={overall} currency={overall.currency} />)}
            </div>
          </div>

          {/* Compare two periods */}
          {data.periods.length >= 1 && (
            <div data-testid="compare-section">
              <h2 className="mb-3 text-sm font-bold text-[#17201A]">Comparer deux périodes</h2>
              <div className="mb-4 flex flex-wrap items-center gap-3">
                <Select value={pa} onValueChange={setPa}>
                  <SelectTrigger className="h-9 w-[180px] text-sm" data-testid="period-a"><SelectValue placeholder="Période A" /></SelectTrigger>
                  <SelectContent>{data.periods.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
                <ArrowRight size={16} className="text-slate-300" />
                <Select value={pb} onValueChange={setPb}>
                  <SelectTrigger className="h-9 w-[180px] text-sm" data-testid="period-b"><SelectValue placeholder="Période B" /></SelectTrigger>
                  <SelectContent>{data.periods.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
              </div>

              {blockA && blockB ? (
                <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white" data-testid="compare-table">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                        <th className="px-5 py-3">Indicateur</th>
                        <th className="px-5 py-3">{pa}</th>
                        <th className="px-5 py-3">{pb}</th>
                        <th className="px-5 py-3">Évolution</th>
                      </tr>
                    </thead>
                    <tbody>
                      {METRICS.map((m) => {
                        const ca = m.get(blockA), cb = m.get(blockB);
                        const bothNumeric = ca.value != null && cb.value != null;
                        return (
                          <tr key={m.key} data-testid={`compare-row-${m.key}`} className="border-b border-slate-100 last:border-0">
                            <td className="px-5 py-3 text-sm font-medium text-[#17201A]">{m.label}</td>
                            <td className="px-5 py-3 text-sm"><span className="text-[#17201A]">{m.fmt(blockA, { currency: blockA.currency })}</span> <StatusChip status={ca.status} /></td>
                            <td className="px-5 py-3 text-sm"><span className="text-[#17201A]">{m.fmt(blockB, { currency: blockB.currency })}</span> <StatusChip status={cb.status} /></td>
                            <td className="px-5 py-3">{bothNumeric ? <Delta a={ca.value} b={cb.value} kind={kindOf(m.key)} /> : <span className="text-xs text-slate-300">n/a</span>}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-sm text-slate-400" data-testid="compare-hint">Sélectionnez deux périodes pour les comparer.</p>
              )}
            </div>
          )}
        </>
      )}

      {/* Scheduled Reports (mocked email delivery) */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="scheduled-reports">
        <div className="mb-1 flex items-center gap-2">
          <CalendarClock size={18} className="text-[#3E7256]" />
          <h2 className="font-heading text-lg font-bold text-[#17201A]">Rapports programmés</h2>
        </div>
        <p className="mb-4 text-xs text-slate-500">Envoyez automatiquement le rapport Performance à vos boards & partenaires. <span className="font-semibold text-[#B45309]">Envoi simulé (MOCKED) pour cette V1.</span></p>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-400">Fréquence</label>
            <Select value={sched.frequency} onValueChange={(v) => saveSchedule({ frequency: v })} disabled={!admin}>
              <SelectTrigger className="h-9 text-sm" data-testid="report-frequency"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="weekly">Hebdomadaire</SelectItem>
                <SelectItem value="monthly">Mensuelle</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2">
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-400">Destinataires (séparés par des virgules)</label>
            <input data-testid="report-recipients" value={recipientsText} onChange={(e) => setRecipientsText(e.target.value)} disabled={!admin}
              placeholder="board@exemple.com, partenaire@exemple.com" className="h-9 w-full rounded-lg border border-slate-200 px-3 text-sm outline-none focus:border-[#5C9873] disabled:bg-slate-50" />
          </div>
        </div>

        {admin && !readOnly && (
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <label className="flex items-center gap-2 text-sm text-[#374151]">
              <input type="checkbox" data-testid="report-enabled" checked={sched.enabled} onChange={(e) => saveSchedule({ enabled: e.target.checked })} className="h-4 w-4 accent-[#3E7256]" />
              Planification activée
            </label>
            <div className="flex-1" />
            <button data-testid="report-save" onClick={() => saveSchedule({})} className="inline-flex items-center gap-1.5 rounded-lg border border-[#E8E5DD] bg-white px-3 py-2 text-sm font-semibold text-[#475569] hover:bg-[#FAFAF6]"><Save size={15} /> Enregistrer</button>
            <button data-testid="report-send-now" onClick={sendNow} className="inline-flex items-center gap-1.5 rounded-lg bg-[#1E3D2B] px-3 py-2 text-sm font-semibold text-white hover:bg-[#2C5540]"><Send size={15} /> Envoyer maintenant</button>
          </div>
        )}
        {admin && readOnly && (
          <div className="mt-4" data-testid="report-readonly">
            <button data-testid="report-send-now-disabled" disabled className="inline-flex items-center gap-1.5 rounded-lg bg-slate-200 px-3 py-2 text-sm font-semibold text-slate-400 cursor-not-allowed"><Send size={15} /> Envoyer maintenant</button>
            <DemoHint />
          </div>
        )}

        {sched.last_sent_at && <p className="mt-3 text-xs text-slate-400">Dernier envoi : {new Date(sched.last_sent_at).toLocaleString("fr-FR")}</p>}

        {sends.length > 0 && (
          <div className="mt-4 border-t border-slate-100 pt-3" data-testid="report-sends">
            <p className="mb-2 text-[11px] font-bold uppercase tracking-wider text-slate-400">Historique des envois (simulés)</p>
            <ul className="space-y-1.5">
              {sends.map((s) => (
                <li key={s.id} data-testid={`report-send-${s.id}`} className="flex items-center justify-between text-xs text-slate-600">
                  <span>{new Date(s.created_at).toLocaleString("fr-FR")} · {s.frequency} · {s.recipients.length} destinataire{s.recipients.length > 1 ? "s" : ""}</span>
                  <span className="rounded-full bg-[#FEF3C7] px-2 py-0.5 text-[10px] font-bold text-[#B45309]">MOCKED</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
